import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * Identify & Filter SD Customer Step
 *
 * Single step that identifies which customer sent a Ship and Debit email
 * AND verifies the email matches that customer's 849 detection patterns.
 *
 * Flow:
 * 1. Match the sender address against each customer's senderPatterns
 * 2. If a customer is found, check subject/body patterns for that customer
 * 3. Output matched=true only if both sender AND subject/body patterns match
 *
 * Pattern matching:
 * - Each pattern is tried as regex first (case-insensitive), falling back to substring match
 * - Within each field group, patterns use OR logic (any pattern can match)
 * - Across field groups (subject, body), only configured groups are checked (AND logic)
 */
export const identifyAndFilterSDStep = defineStepConfig({
  type: "identify_and_filter_sd",
  name: "Identify & Filter SD Customer",
  description:
    "Identify which customer sent a Ship and Debit email and verify it matches their 849 detection patterns",
  category: "Transformation",
  libraryModule: "ShipAndDebit",
  inputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "The inbound email to identify and filter",
    ),
  }),

  outputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "Pass-through of the inbound email",
    ),
    matched: z
      .boolean()
      .describe(
        "Whether the email matched a customer's sender AND 849 detection patterns",
      ),
    customerId: z
      .string()
      .nullable()
      .describe("Customer identifier, null if not matched"),
    customerName: z
      .string()
      .nullable()
      .describe("Human-readable customer name, null if not matched"),
    processorType: z
      .string()
      .nullable()
      .describe("Which SD processor to use, null if not matched"),
    customerConfig: z
      .record(z.string(), z.unknown())
      .nullable()
      .describe("Customer-specific configuration for the processor"),
    confidence: z
      .number()
      .min(0)
      .max(1)
      .describe("Confidence score (1.0 if matched, 0.0 if not)"),
    matchDetails: z
      .string()
      .describe("Human-readable summary of the matching result"),
  }),

  configSchema: z.object({
    customerMappings: z
      .record(
        z.string(),
        z.object({
          senderPatterns: z.array(z.string()).optional(),
          subjectPatterns: z.array(z.string()).optional(),
          bodyPatterns: z.array(z.string()).optional(),
          processorType: z.enum(["config", "generic", "spsx"]).optional(),
        }),
      )
      .optional()
      .describe(
        "Map of customer name → { senderPatterns, subjectPatterns, bodyPatterns, processorType }. The sender email identifies the customer, then subject/body patterns confirm it's an 849 response.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 seconds",
    retry: {
      maximumAttempts: 2,
    },
  },
});
